import type { ReactNode } from "react";

import { FadersHorizontalIcon } from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Popover,
  ScrollArea,
  Separator,
  Text,
} from "@radix-ui/themes";

export interface ColumnsPopoverProps {
  /** Columns the picker controls, not counting any the widget pins open. */
  visibleCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onReset: () => void;
  /** Disables the whole trigger, for a density that has no column choice. */
  isDisabled?: boolean;
  /** The checkbox list. Layout is the caller's, so groups and nesting are free. */
  children: ReactNode;
  /** Footnote under the list, for whatever the caller will not let be hidden. */
  footnote?: ReactNode;
  /**
   * `labeled` is the counted "Columns" chip. `icon` is the prototype mixer
   * button (`aria-label="Configure columns"`).
   */
  triggerVariant?: "labeled" | "icon";
  onOpenChange?: (open: boolean) => void;
}

/**
 * Shared chrome for a widget's column picker: the counted trigger, the heading,
 * the select-all and reset pair, and the scrolling list. Only the list itself
 * differs between widgets, so it comes in as children.
 */
export const ColumnsPopover = ({
  visibleCount,
  totalCount,
  onSelectAll,
  onReset,
  isDisabled,
  children,
  footnote,
  triggerVariant = "labeled",
  onOpenChange,
}: ColumnsPopoverProps) => (
  <Popover.Root onOpenChange={onOpenChange}>
    <Popover.Trigger>
      {triggerVariant === "icon" ? (
        <IconButton
          type="button"
          size="1"
          variant="ghost"
          color="gray"
          disabled={isDisabled}
          aria-label="Configure columns"
        >
          <FadersHorizontalIcon size={14} aria-hidden />
        </IconButton>
      ) : (
        <Button
          type="button"
          size="1"
          variant="soft"
          color="gray"
          disabled={isDisabled}
          aria-label={`Columns, ${visibleCount} of ${totalCount} visible`}
        >
          Columns
          <Badge color="gray" variant="solid">
            {visibleCount} of {totalCount}
          </Badge>
        </Button>
      )}
    </Popover.Trigger>
    <Popover.Content width="320px" maxWidth="calc(100vw - 2rem)">
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between" gap="2">
          <Heading as="h3" size="3">
            Columns
          </Heading>
          <Text size="1" color="gray" className="atd-numeric">
            {visibleCount} of {totalCount} visible
          </Text>
        </Flex>

        <Flex align="center" gap="2" wrap="wrap">
          <Button
            type="button"
            size="1"
            variant="soft"
            color="gray"
            onClick={onSelectAll}
            disabled={visibleCount === totalCount}
          >
            Select all
          </Button>
          <Button
            type="button"
            size="1"
            variant="soft"
            color="gray"
            onClick={onReset}
          >
            Reset defaults
          </Button>
        </Flex>

        <Separator size="4" />

        <ScrollArea type="auto" scrollbars="vertical" className="max-h-[22rem]">
          <Box pr="2">
            <Flex direction="column" gap="2">
              {children}
            </Flex>
          </Box>
        </ScrollArea>

        {footnote}
      </Flex>
    </Popover.Content>
  </Popover.Root>
);
